import { useTranslation } from 'react-i18next';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/store/api';
import {
  Drawer,
  Button,
  Typography,
  Box,
  Snackbar,
  CircularProgress,
  DropdownField,
  Link,
  type DropdownOption,
} from '@gapartners/ui';
import { CaseNotesAppendEditor } from '@/components/contact-center/CaseNotesAppendEditor';
import { ConfirmDiscardChangesDialog } from '@/components/contact-center/ConfirmDiscardChangesDialog';
import { LinkCaseDialog } from '@/components/contact-center/LinkCaseDialog';
import { CaseTimeTrackingSection } from '@/components/contact-center/CaseTimeTrackingSection';
import {
  isCaseClosed,
  REPORTING_METHOD_OPTIONS,
} from '@/components/contact-center/caseFormConstants';
import type {
  CaseCategory,
  CaseInformationResponse,
  CloseCaseLoop,
} from '@/types/case';
import type { CustomerCase } from '@/types/customer';
import { getClientSpaceSubdomain } from '@/utils/clientSpace';

// ============================================================================
// Style Constants
// ============================================================================

const LABEL_BOX_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  mb: 0.5,
};

const FOCUS_STYLE = {
  outline: 'none',
  borderColor: '#1976d2',
  boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)',
};

const MIN_DRAWER_WIDTH = 150;
const DEFAULT_DRAWER_WIDTH = 320;
const DRAWER_WIDTH_STORAGE_KEY = 'esp.caseForm.drawerWidth';

function loadDrawerWidth(): number {
  try {
    const raw = window.localStorage.getItem(DRAWER_WIDTH_STORAGE_KEY);
    const parsed = raw == null ? NaN : Number(raw);
    if (Number.isFinite(parsed)) {
      return Math.max(parsed, MIN_DRAWER_WIDTH);
    }
  } catch {
    // localStorage unavailable (private mode / tests) — fall through
  }
  return DEFAULT_DRAWER_WIDTH;
}

const INPUT_STYLE = {
  padding: '8px 12px',
  border: '1px solid #ccc',
  borderRadius: '4px',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
  width: '100%',
  '&:focus': FOCUS_STYLE,
};

const TEXTAREA_STYLE = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #ccc',
  borderRadius: '4px',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
  resize: 'vertical',
  '&:focus': FOCUS_STYLE,
};

const SELECT_FIELD_STYLE = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    fontSize: '0.875rem',
    minHeight: '36px',
    '& .MuiOutlinedInput-input': {
      padding: '6px 12px',
    },
  },
};

const CHANGED_FIELD_SX = {
  borderColor: '#ed6c02',
  backgroundColor: 'rgba(237, 108, 2, 0.08)',
};

const CHANGED_SELECT_FIELD_SX = {
  backgroundColor: 'rgba(237, 108, 2, 0.08)',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ed6c02',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ed6c02',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ed6c02',
  },
};

const DISABLED_FIELD_WRAPPER_SX = {
  opacity: 0.55,
};

// Two-column grid for the form body
const TWO_COL_GRID = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 1.5,
};

/** DropdownField wrapper that works with raw ID values and standard {value,label} options. */
function CaseDropdown({
  value,
  onChange,
  options,
  disabled,
  sx,
}: {
  value: string | number | null;
  onChange: (id: string | number | null) => void;
  options: Array<{
    value: string | number;
    label: string;
    id?: string | number;
  }>;
  disabled?: boolean;
  sx?: object;
}) {
  const withId = options.map(o => ({ ...o, id: o.id ?? o.value }));
  const selected =
    withId.find(o => o.id === value && value !== '' && value != null) ?? null;

  return (
    <DropdownField
      label=""
      multiple={false}
      options={withId}
      value={selected}
      onChange={(_e, val) => {
        if (Array.isArray(val)) return;
        const id = val && 'id' in val ? (val as DropdownOption).id : null;
        onChange(id != null && id !== '' ? id : null);
      }}
      disabled={disabled}
      getOptionId={o => o.id}
      getOptionLabel={o => o.label}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      filterOption={(o, input) =>
        String(o.label).toLowerCase().includes(input.toLowerCase())
      }
      // Commit the arrow-key-highlighted option when the field loses focus
      // (e.g. Tab to the next field), instead of requiring a mouse click.
      autoSelect
      sx={sx}
      fullWidth
    />
  );
}

function toUniqueStringOptions(
  values: string[]
): Array<{ value: string; label: string }> {
  const seen = new Set<string>();
  return values.reduce<Array<{ value: string; label: string }>>(
    (options, value) => {
      if (seen.has(value)) return options;
      seen.add(value);
      options.push({ value, label: value });
      return options;
    },
    []
  );
}

// ============================================================================
// EditableCaseField Component
// ============================================================================

export interface EditableCaseFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hasChanged: boolean;
  disabled?: boolean;
  multiline?: boolean;
  type?: 'text' | 'email' | 'tel';
  placeholder?: string;
  headerAction?: ReactNode;
  children?: ReactNode;
}

export function EditableCaseField({
  label,
  value,
  onChange,
  hasChanged,
  disabled = false,
  multiline = false,
  type = 'text',
  placeholder = '',
  headerAction,
  children,
}: EditableCaseFieldProps) {
  return (
    <Box sx={disabled ? DISABLED_FIELD_WRAPPER_SX : undefined}>
      <Box
        sx={{
          ...LABEL_BOX_STYLE,
          ...(headerAction && { justifyContent: 'space-between' }),
        }}
      >
        <Typography variant="body2" component="label" fontWeight={600}>
          {label}:
        </Typography>
        {headerAction}
      </Box>
      {children ? (
        children
      ) : (
        <Box
          component={multiline ? 'textarea' : 'input'}
          type={multiline ? undefined : type}
          value={value}
          onChange={(
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
          ) =>
            onChange(
              multiline
                ? (e.target as HTMLTextAreaElement).value
                : (e.target as HTMLInputElement).value
            )
          }
          placeholder={placeholder}
          disabled={disabled}
          sx={{
            ...INPUT_STYLE,
            ...(multiline && {
              minHeight: '60px',
              resize: 'vertical',
            }),
            ...(hasChanged && CHANGED_FIELD_SX),
            ...(disabled && {
              backgroundColor: '#f5f5f5',
              cursor: 'not-allowed',
            }),
          }}
        />
      )}
    </Box>
  );
}

// ============================================================================
// CaseFormDialog Component
// ============================================================================

export type CaseFieldType =
  | 'status'
  | 'subject'
  | 'description'
  | 'notes'
  | 'category'
  | 'categoryPriority'
  | 'caseType'
  | 'subCaseType'
  | 'closeCaseLoop'
  | 'resolution'
  | 'resolutionType'
  | 'phoneNumber'
  | 'email'
  | 'origination'
  | 'employeeId'
  | 'reportedByEmployeeId'
  | 'assignedTo'
  | 'reportedByName'
  | 'reportingMethod'
  | 'clientContactId';

export interface CaseFormFields {
  subject: string;
  description: string;
  status: string;
  category: string;
  categoryCode: string;
  categoryName: string;
  categoryPriority: string;
  caseType: string;
  caseTypeId: number | null;
  subCaseType: string;
  origination: string;
  closeCaseLoop: CloseCaseLoop;
  resolution: string;
  resolutionType: string;
  phoneNumber: string;
  email: string;
  reportingMethod: string;
  notes: string;
  employeeId: number | null;
  reportedByEmployeeId: number | null;
  assignedTo: number | null;
  reportedByName: string;
  clientContactId: string;
}

export interface CaseFormDialogProps {
  mode: 'create' | 'edit';
  open: boolean;
  onClose: () => void;
  onSubmit: () => void | Promise<void>;
  isLoading: boolean;
  formFields: CaseFormFields;
  onFormFieldChange: (
    field: keyof CaseFormFields,
    value: string | number | null
  ) => void;
  statusOptions: Array<{ value: string; label: string }>;
  subCaseTypeTree: Record<string, string[]>;
  originationOptions: Array<{ value: string; label: string }>;
  priorityOptions: Array<{ value: string; label: string }>;
  caseTypeOptions?: Array<{ value: number; label: string }>;
  categories?: CaseCategory[];
  currentCase?: CaseInformationResponse | null;
  initialCaseFields?: Partial<CaseFormFields>;
  hasFieldChanged?: (fieldType: CaseFieldType) => boolean;
  hasUnsavedChanges?: boolean;
  onDiscardNotes?: () => void;
  contactId?: string | null;
  isCaseLoading?: boolean;
  caseLoadError?: boolean;
  closeCaseLoopSource?: 'activeCall' | 'caseRecord';
  activeCallCloseCaseLoop?: CloseCaseLoop | null;
  employeeOptions?: Array<{
    id: number;
    value: number;
    label: string;
    displayName?: string;
    phone?: string;
    email?: string;
  }>;
  internalUserOptions?: Array<{ id: number; value: number; label: string }>;
  resolutionTypeOptions?: Array<{ value: string; label: string }>;
  /** Caller's name displayed as read-only "Reported By Name". */
  callerName?: string;
  /** When Reported By Employee changes, phone/email get updated. */
  onContactChanged?: (contact: { phone?: string; email?: string }) => void;
  /** Client contacts dropdown options for Client Request origination. */
  clientContactOptions?: Array<{ value: string; label: string }>;
  /**
   * Workspace whose cases can be picked as the related case. Enables the
   * "Link to Related Case" header action. In edit mode the link is applied
   * immediately (requires `contactId`); in create mode the selection is sent
   * with the new case.
   */
  relatedCaseWorkspaceId?: string | null;
  /** Related case selected on the create form (create mode only). */
  relatedCase?: CustomerCase | null;
  /** Selects or clears the related case (create mode only). */
  onRelatedCaseChange?: (relatedCase: CustomerCase | null) => void;
}

export function CaseFormDialog({
  mode,
  open,
  onClose,
  onSubmit,
  isLoading,
  formFields,
  onFormFieldChange,
  statusOptions,
  subCaseTypeTree,
  originationOptions,
  priorityOptions,
  caseTypeOptions = [],
  categories = [],
  currentCase,
  initialCaseFields = {},
  hasFieldChanged = () => false,
  hasUnsavedChanges = false,
  onDiscardNotes = () => {},
  contactId,
  isCaseLoading = false,
  caseLoadError = false,
  employeeOptions = [],
  internalUserOptions = [],
  resolutionTypeOptions = [],
  clientContactOptions = [],
  relatedCaseWorkspaceId = null,
  relatedCase = null,
  onRelatedCaseChange,
  onContactChanged,
}: CaseFormDialogProps) {
  const { t } = useTranslation();
  const [fetchCaseResolution, { isFetching: isFillingCaseResolution }] =
    api.useLazyGetCaseResolutionQuery();
  const [linkRelatedCase, { isLoading: isLinkingRelatedCase }] =
    api.useLinkRelatedCaseMutation();
  const [isRelatedCasePickerOpen, setIsRelatedCasePickerOpen] = useState(false);
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning';
  } | null>(null);
  const [drawerWidth, setDrawerWidth] = useState(loadDrawerWidth);
  const widthRef = useRef(drawerWidth);

  useEffect(() => {
    if (!open) {
      // The parent can close the drawer itself (case closed, contact switched).
      setIsDiscardConfirmOpen(false);
      return;
    }
    setDrawerWidth(loadDrawerWidth());
  }, [open]);

  const handleResizeStart = (e: React.PointerEvent) => {
    e.preventDefault();
    widthRef.current = drawerWidth;
    const move = (ev: PointerEvent) => {
      const next = Math.min(
        Math.max(window.innerWidth - ev.clientX, MIN_DRAWER_WIDTH),
        window.innerWidth
      );
      widthRef.current = next;
      setDrawerWidth(next);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.userSelect = '';
      try {
        window.localStorage.setItem(
          DRAWER_WIDTH_STORAGE_KEY,
          String(widthRef.current)
        );
      } catch {
        // ignore storage failures
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    document.body.style.userSelect = 'none';
  };

  // Closing the edit drawer drops unsaved edits (the form resyncs from the case
  // on reopen), so confirm first. A save in flight is not treated as unsaved.
  const handleRequestClose = () => {
    if (mode === 'edit' && hasUnsavedChanges && !isLoading) {
      setIsDiscardConfirmOpen(true);
      return;
    }
    onClose();
  };

  const handleDiscardAndClose = () => {
    setIsDiscardConfirmOpen(false);
    onClose();
  };

  const handleFillCaseResolution = useCallback(async () => {
    if (!contactId) return;
    try {
      const { resolution } = await fetchCaseResolution({ contactId }).unwrap();
      onFormFieldChange('resolution', resolution);
    } catch {
      setToast({
        message: t('components.contactCenter.errorFillingCaseResolution'),
        type: 'error',
      });
    }
  }, [contactId, fetchCaseResolution, onFormFieldChange, t]);

  const canLinkRelatedCase =
    !!relatedCaseWorkspaceId &&
    (mode === 'create'
      ? !!onRelatedCaseChange
      : currentCase?.id != null && !!contactId);

  const handleRelatedCaseSelected = async (caseItem: CustomerCase) => {
    if (mode === 'create') {
      onRelatedCaseChange?.(caseItem);
      setIsRelatedCasePickerOpen(false);
      return;
    }
    if (currentCase?.id == null || !contactId) return;
    try {
      const result = await linkRelatedCase({
        caseId: currentCase.id,
        relatedCaseId: Number(caseItem.id),
        contactId,
      }).unwrap();
      setIsRelatedCasePickerOpen(false);
      setToast(
        result.relatedCaseUpdated
          ? {
              message: t('components.contactCenter.relatedCaseLinkedOpen', {
                caseNumber: result.relatedCaseNumber,
              }),
              type: 'success',
            }
          : {
              message: t('components.contactCenter.relatedCaseLinkedResolved', {
                caseNumber: result.relatedCaseNumber,
              }),
              type: 'warning',
            }
      );
    } catch {
      setToast({
        message: t('components.contactCenter.relatedCaseLinkFailed'),
        type: 'error',
      });
    }
  };

  const fillCaseResolutionButton = (
    <Button
      onClick={handleFillCaseResolution}
      variant="outlined"
      size="small"
      disabled={!contactId || isFillingCaseResolution}
    >
      {t('components.contactCenter.fillCaseResolution')}
    </Button>
  );

  const handleCategorySelect = (categoryCode: string) => {
    const selectedCategory = categories.find(cat => cat.code === categoryCode);
    const isCategoryChanged = categoryCode !== formFields.categoryCode;
    onFormFieldChange('categoryName', selectedCategory?.name || '');
    onFormFieldChange('category', selectedCategory?.name || '');
    onFormFieldChange('categoryCode', selectedCategory?.code ?? '');
    if (isCategoryChanged) onFormFieldChange('subCaseType', '');
  };

  const categorySelectOptions = [...categories]
    .sort((a, b) => {
      const aCount = subCaseTypeTree[a.code]?.length ?? 0;
      const bCount = subCaseTypeTree[b.code]?.length ?? 0;
      if (aCount !== bCount) return aCount > bCount ? -1 : 1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    })
    .map(cat => ({
      value: cat.code,
      label:
        (subCaseTypeTree[cat.code]?.length ?? 0) > 0
          ? `${cat.name} (${subCaseTypeTree[cat.code]!.length} sub-case types)`
          : cat.name,
    }));

  const subCaseTypeValues = formFields.categoryCode
    ? (subCaseTypeTree[formFields.categoryCode] ?? [])
    : [];
  const subCaseTypeOptions = toUniqueStringOptions(subCaseTypeValues);
  const isSubCaseTypeDisabled = subCaseTypeOptions.length === 0;

  const handleCaseTypeSelect = (caseTypeId: string | number) => {
    const selectedId = caseTypeId === '' ? null : Number(caseTypeId);
    const selected = caseTypeOptions.find(o => o.value === selectedId);
    onFormFieldChange('caseTypeId', selected?.value ?? null);
    onFormFieldChange('caseType', selected?.label ?? '');
  };

  const isReportedByEmployeeSelected = formFields.reportedByEmployeeId != null;

  const handleReportedBySelect = (employeeId: string | number) => {
    const id =
      employeeId === '' || employeeId == null ? null : Number(employeeId);
    onFormFieldChange('reportedByEmployeeId', id);
    if (id != null) {
      const emp = employeeOptions.find(o => o.value === id);
      if (emp) {
        onFormFieldChange('phoneNumber', emp.phone ?? '');
        onFormFieldChange('email', emp.email ?? '');
        onFormFieldChange('reportedByName', emp.displayName ?? '');
        onContactChanged?.({
          phone: emp.phone ?? '',
          email: emp.email ?? '',
        });
      }
    }
  };

  if (mode === 'edit' && !currentCase && !isCaseLoading && !caseLoadError) {
    return null;
  }

  const clientSpaceSubdomain = getClientSpaceSubdomain();

  const title =
    mode === 'create' ? (
      t('components.contactCenter.createNewCase')
    ) : currentCase?.id != null ? (
      <Link
        href={`https://${clientSpaceSubdomain}.clientspace.net/next/linkto/ClientServiceCase/${currentCase.id}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t('components.contactCenter.case')} {currentCase.id}
      </Link>
    ) : (
      t('components.contactCenter.caseDetails')
    );

  const isClosing = mode === 'edit' && isCaseClosed(formFields.status);

  const submitButtonLabel =
    mode === 'create'
      ? isLoading
        ? t('common.creating')
        : t('components.contactCenter.createCase')
      : isLoading
        ? t('components.contactCenter.saving')
        : isClosing
          ? t('components.contactCenter.closeCase')
          : t('common.save');

  const getSelectFieldSx = (fieldType: CaseFieldType) => ({
    ...SELECT_FIELD_STYLE,
    ...(hasFieldChanged(fieldType) && CHANGED_SELECT_FIELD_SX),
  });

  // ── shared field renderers ──────────────────────────────────────────

  const renderFullWidth = (child: ReactNode) => (
    <Box sx={{ gridColumn: '1 / -1' }}>{child}</Box>
  );

  const renderCreateFields = () => {
    const labelSx = { mb: 0.5, fontWeight: 600, fontSize: '0.8125rem' };
    const reqMark = (label: string) => (
      <Typography variant="body2" component="label" sx={labelSx}>
        {label}{' '}
        <Box component="span" sx={{ color: 'error.main' }}>
          *
        </Box>
        :
      </Typography>
    );
    const plainLabel = (label: string) => (
      <Typography variant="body2" component="label" sx={labelSx}>
        {label}:
      </Typography>
    );

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* ── TOP SECTION ── */}
        <Box sx={TWO_COL_GRID}>
          {/* R1: Category — Case Type */}
          <Box>
            {reqMark(t('components.contactCenter.category'))}
            <CaseDropdown
              value={formFields.categoryCode}
              onChange={id => handleCategorySelect(id as string)}
              options={categorySelectOptions}
              sx={SELECT_FIELD_STYLE}
            />
          </Box>
          <Box>
            {reqMark(t('components.contactCenter.caseType'))}
            <CaseDropdown
              value={formFields.caseTypeId ?? ''}
              onChange={id => handleCaseTypeSelect(id as string)}
              options={
                caseTypeOptions.length > 0
                  ? caseTypeOptions
                  : [{ value: 0, label: '' }]
              }
              sx={SELECT_FIELD_STYLE}
            />
          </Box>

          {/* R2: Origination — Sub Case Type */}
          <Box>
            {reqMark(t('components.contactCenter.origination'))}
            <CaseDropdown
              value={formFields.origination}
              onChange={id => onFormFieldChange('origination', id as string)}
              options={originationOptions}
              sx={SELECT_FIELD_STYLE}
            />
          </Box>
          <Box
            sx={isSubCaseTypeDisabled ? DISABLED_FIELD_WRAPPER_SX : undefined}
          >
            {plainLabel(t('components.contactCenter.subCaseType'))}
            <CaseDropdown
              value={formFields.subCaseType}
              onChange={id => onFormFieldChange('subCaseType', id as string)}
              options={subCaseTypeOptions}
              disabled={isSubCaseTypeDisabled}
              sx={SELECT_FIELD_STYLE}
            />
          </Box>

          {/* R3: Reporting Method (full width) */}
          <Box sx={{ gridColumn: '1 / -1' }}>
            {plainLabel(t('components.contactCenter.reportingMethod'))}
            <CaseDropdown
              value={formFields.reportingMethod}
              onChange={id =>
                onFormFieldChange('reportingMethod', id as string)
              }
              options={REPORTING_METHOD_OPTIONS}
              sx={SELECT_FIELD_STYLE}
            />
          </Box>

          {/* R4: Assigned To (full width) */}
          {renderFullWidth(
            <Box>
              {plainLabel(t('components.contactCenter.assignedTo'))}
              <CaseDropdown
                value={formFields.assignedTo ?? ''}
                onChange={id =>
                  onFormFieldChange(
                    'assignedTo',
                    typeof id === 'string' && id === '' ? null : Number(id)
                  )
                }
                options={internalUserOptions}
                sx={SELECT_FIELD_STYLE}
              />
            </Box>
          )}

          {/* R5: Status — Priority */}
          <Box>
            {reqMark(t('components.contactCenter.status'))}
            <CaseDropdown
              value={formFields.status}
              onChange={id => onFormFieldChange('status', id as string)}
              options={statusOptions}
              sx={SELECT_FIELD_STYLE}
            />
          </Box>
          <Box>
            {reqMark(t('components.contactCenter.priority'))}
            <CaseDropdown
              value={formFields.categoryPriority}
              onChange={id =>
                onFormFieldChange('categoryPriority', id as string)
              }
              options={priorityOptions}
              sx={SELECT_FIELD_STYLE}
            />
          </Box>

          {/* R6: Subject (full width) */}
          {renderFullWidth(
            <Box>
              <Typography
                variant="body2"
                component="label"
                sx={{
                  ...labelSx,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {t('components.contactCenter.subject')}:
              </Typography>
              <Box
                component="input"
                type="text"
                value={formFields.subject}
                onChange={e => onFormFieldChange('subject', e.target.value)}
                sx={INPUT_STYLE}
              />
            </Box>
          )}

          {/* R7: Issue / Description (full width) */}
          {renderFullWidth(
            <Box>
              <Typography
                variant="body2"
                component="label"
                sx={{
                  ...labelSx,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {t('components.contactCenter.issue')}:
              </Typography>
              <Box
                component="textarea"
                value={formFields.description}
                onChange={e => onFormFieldChange('description', e.target.value)}
                rows={5}
                sx={TEXTAREA_STYLE}
              />
            </Box>
          )}

          {/* R8: Internal Notes (full width) */}
          {renderFullWidth(
            <Box>
              <Typography
                variant="body2"
                component="label"
                sx={{
                  ...labelSx,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {t('components.contactCenter.internalNotes')}:
              </Typography>
              <Box
                component="textarea"
                value={formFields.notes}
                onChange={e => onFormFieldChange('notes', e.target.value)}
                rows={3}
                sx={TEXTAREA_STYLE}
              />
            </Box>
          )}

          {/* R9: Related Case picked via the header action (full width) */}
          {relatedCase &&
            renderFullWidth(
              <Box>
                {plainLabel(t('components.contactCenter.relatedCase'))}
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Typography variant="body2">
                    {relatedCase.caseId} — {relatedCase.description}
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => onRelatedCaseChange?.(null)}
                    aria-label={t('components.contactCenter.clearRelatedCase')}
                    sx={{ minWidth: 0, px: 1, fontSize: '0.75rem' }}
                  >
                    ✕
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {t('components.contactCenter.relatedCaseCreateHint')}
                </Typography>
              </Box>
            )}
        </Box>

        {/* ── GREEN SECTION: Client / Employee Details ── */}
        <Box
          sx={{
            p: 2,
            bgcolor: '#e8f5e9',
            borderRadius: 1,
            border: '1px solid #a5d6a7',
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            color="success.dark"
            sx={{
              mb: 1.5,
              display: 'block',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {t('components.contactCenter.clientEmployeeDetails')}
          </Typography>
          <Box sx={TWO_COL_GRID}>
            <Box
              sx={
                isReportedByEmployeeSelected
                  ? DISABLED_FIELD_WRAPPER_SX
                  : undefined
              }
            >
              {plainLabel(t('components.contactCenter.reportedByName'))}
              <Box
                component="input"
                type="text"
                value={formFields.reportedByName}
                onChange={e =>
                  onFormFieldChange('reportedByName', e.target.value)
                }
                disabled={isReportedByEmployeeSelected}
                sx={{
                  ...INPUT_STYLE,
                  ...(isReportedByEmployeeSelected && {
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed',
                  }),
                }}
              />
            </Box>
            <Box>
              {plainLabel(t('components.contactCenter.reportedByEmployee'))}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <CaseDropdown
                  value={formFields.reportedByEmployeeId ?? ''}
                  onChange={id => handleReportedBySelect(id as string)}
                  options={employeeOptions}
                  sx={{ ...SELECT_FIELD_STYLE, flex: 1 }}
                />
                {formFields.reportedByEmployeeId != null && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() =>
                      onFormFieldChange('reportedByEmployeeId', null)
                    }
                    sx={{ minWidth: 0, px: 1, fontSize: '0.75rem' }}
                  >
                    ✕
                  </Button>
                )}
              </Box>
            </Box>
            <Box
              sx={
                isReportedByEmployeeSelected
                  ? DISABLED_FIELD_WRAPPER_SX
                  : undefined
              }
            >
              {plainLabel(t('components.contactCenter.phoneNumber'))}
              <Box
                component="input"
                type="tel"
                value={formFields.phoneNumber}
                onChange={e => onFormFieldChange('phoneNumber', e.target.value)}
                disabled={isReportedByEmployeeSelected}
                sx={{
                  ...INPUT_STYLE,
                  ...(isReportedByEmployeeSelected && {
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed',
                  }),
                }}
              />
            </Box>
            <Box
              sx={
                isReportedByEmployeeSelected
                  ? DISABLED_FIELD_WRAPPER_SX
                  : undefined
              }
            >
              {plainLabel(t('components.contactCenter.email'))}
              <Box
                component="input"
                type="email"
                value={formFields.email}
                onChange={e => onFormFieldChange('email', e.target.value)}
                disabled={isReportedByEmployeeSelected}
                sx={{
                  ...INPUT_STYLE,
                  ...(isReportedByEmployeeSelected && {
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed',
                  }),
                }}
              />
            </Box>
            <Box>
              {plainLabel(t('components.contactCenter.employee'))}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <CaseDropdown
                  value={formFields.employeeId ?? ''}
                  onChange={id =>
                    onFormFieldChange(
                      'employeeId',
                      typeof id === 'string' && id === '' ? null : Number(id)
                    )
                  }
                  options={employeeOptions}
                  sx={{ ...SELECT_FIELD_STYLE, flex: 1 }}
                />
                {formFields.employeeId != null && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => onFormFieldChange('employeeId', null)}
                    sx={{ minWidth: 0, px: 1, fontSize: '0.75rem' }}
                  >
                    ✕
                  </Button>
                )}
              </Box>
            </Box>
            <Box>
              {plainLabel(t('components.contactCenter.clientContact'))}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <CaseDropdown
                  value={formFields.clientContactId || ''}
                  onChange={id =>
                    onFormFieldChange(
                      'clientContactId',
                      id == null || id === '' ? '' : String(id)
                    )
                  }
                  options={clientContactOptions}
                  sx={{ ...SELECT_FIELD_STYLE, flex: 1 }}
                />
                {formFields.clientContactId && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => onFormFieldChange('clientContactId', '')}
                    sx={{ minWidth: 0, px: 1, fontSize: '0.75rem' }}
                  >
                    ✕
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ── PINK SECTION: Resolution ── */}
        <Box
          sx={{
            p: 2,
            bgcolor: '#fce4ec',
            borderRadius: 1,
            border: '1px solid #f48fb1',
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            color="error.dark"
            sx={{
              mb: 1.5,
              display: 'block',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {t('components.contactCenter.resolution')}
          </Typography>
          <Box sx={{ ...TWO_COL_GRID, gap: 1.5 }}>
            {renderFullWidth(
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2" component="label" sx={labelSx}>
                    {t('components.contactCenter.resolutionDescription')}:
                  </Typography>
                  {fillCaseResolutionButton}
                </Box>
                <Box
                  component="textarea"
                  value={formFields.resolution}
                  onChange={e =>
                    onFormFieldChange('resolution', e.target.value)
                  }
                  rows={4}
                  sx={TEXTAREA_STYLE}
                />
              </Box>
            )}
            <Box>
              {plainLabel(t('components.contactCenter.closeCaseLoop'))}
              <CaseDropdown
                value={formFields.closeCaseLoop}
                onChange={id =>
                  onFormFieldChange('closeCaseLoop', id as string)
                }
                options={[
                  { value: 'Phone', label: 'Phone' },
                  { value: 'Chat', label: 'Chat' },
                ]}
                sx={SELECT_FIELD_STYLE}
              />
            </Box>
            <Box>
              {plainLabel(t('components.contactCenter.resolutionType'))}
              <CaseDropdown
                value={formFields.resolutionType}
                onChange={id =>
                  onFormFieldChange('resolutionType', id as string)
                }
                options={resolutionTypeOptions}
                sx={SELECT_FIELD_STYLE}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  // ── EDIT MODE ───────────────────────────────────────────────────────

  const renderEditFields = () => {
    const labelSx = { mb: 0.5, fontWeight: 600, fontSize: '0.8125rem' };
    const plainLabel = (label: string) => (
      <Typography variant="body2" component="label" sx={labelSx}>
        {label}:
      </Typography>
    );

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* ── TOP SECTION ── */}
        <Box sx={TWO_COL_GRID}>
          {/* R1: Category — Case Type */}
          <Box>
            {plainLabel(t('components.contactCenter.category'))}
            <CaseDropdown
              value={formFields.categoryCode}
              onChange={id => handleCategorySelect(id as string)}
              options={categorySelectOptions}
              sx={getSelectFieldSx('category')}
            />
          </Box>
          <Box>
            {plainLabel(t('components.contactCenter.caseType'))}
            <CaseDropdown
              value={formFields.caseTypeId ?? ''}
              onChange={id => handleCaseTypeSelect(id as string)}
              options={
                caseTypeOptions.length > 0
                  ? caseTypeOptions
                  : [{ value: 0, label: '' }]
              }
              sx={getSelectFieldSx('caseType')}
            />
          </Box>

          {/* R2: Origination — Sub Case Type */}
          <Box>
            {plainLabel(t('components.contactCenter.origination'))}
            <CaseDropdown
              value={formFields.origination}
              onChange={id => onFormFieldChange('origination', id as string)}
              options={originationOptions}
              sx={getSelectFieldSx('origination')}
            />
          </Box>
          <Box
            sx={isSubCaseTypeDisabled ? DISABLED_FIELD_WRAPPER_SX : undefined}
          >
            {plainLabel(t('components.contactCenter.subCaseType'))}
            <CaseDropdown
              value={formFields.subCaseType}
              onChange={id => onFormFieldChange('subCaseType', id as string)}
              options={subCaseTypeOptions}
              disabled={isSubCaseTypeDisabled}
              sx={getSelectFieldSx('subCaseType')}
            />
          </Box>

          {/* R3: Reporting Method (full width) */}
          <Box sx={{ gridColumn: '1 / -1' }}>
            <EditableCaseField
              label={t('components.contactCenter.reportingMethod')}
              value={formFields.reportingMethod}
              onChange={() => {}}
              hasChanged={hasFieldChanged('reportingMethod')}
            >
              <CaseDropdown
                value={formFields.reportingMethod}
                onChange={id =>
                  onFormFieldChange('reportingMethod', id as string)
                }
                options={REPORTING_METHOD_OPTIONS}
                sx={getSelectFieldSx('reportingMethod')}
              />
            </EditableCaseField>
          </Box>

          {/* R4: Assigned To (full width) */}
          <Box sx={{ gridColumn: '1 / -1' }}>
            <EditableCaseField
              label={t('components.contactCenter.assignedTo')}
              value={String(formFields.assignedTo ?? '')}
              onChange={() => {}}
              hasChanged={hasFieldChanged('assignedTo')}
            >
              <CaseDropdown
                value={formFields.assignedTo ?? ''}
                onChange={id =>
                  onFormFieldChange(
                    'assignedTo',
                    typeof id === 'string' && id === '' ? null : Number(id)
                  )
                }
                options={internalUserOptions}
                sx={getSelectFieldSx('assignedTo')}
              />
            </EditableCaseField>
          </Box>

          {/* R5: Status — Priority */}
          <Box>
            {plainLabel(t('components.contactCenter.status'))}
            <CaseDropdown
              value={formFields.status}
              onChange={id => onFormFieldChange('status', id as string)}
              options={statusOptions}
              sx={getSelectFieldSx('status')}
            />
          </Box>
          <Box>
            {plainLabel(t('components.contactCenter.priority'))}
            <CaseDropdown
              value={formFields.categoryPriority}
              onChange={id =>
                onFormFieldChange('categoryPriority', id as string)
              }
              options={priorityOptions}
              sx={getSelectFieldSx('categoryPriority')}
            />
          </Box>

          {/* R6: Subject (full width) */}
          <Box sx={{ gridColumn: '1 / -1' }}>
            <EditableCaseField
              label={t('components.contactCenter.subject')}
              value={formFields.subject}
              onChange={v => onFormFieldChange('subject', v)}
              hasChanged={hasFieldChanged('subject')}
            />
          </Box>

          {/* R7: Issue / Description (full width) */}
          <Box sx={{ gridColumn: '1 / -1' }}>
            <EditableCaseField
              label={t('components.contactCenter.issue')}
              value={formFields.description}
              onChange={v => onFormFieldChange('description', v)}
              hasChanged={hasFieldChanged('description')}
              multiline
            />
          </Box>

          {/* R8: Internal Notes (full width) */}
          <Box sx={{ gridColumn: '1 / -1' }}>
            <CaseNotesAppendEditor
              savedNotes={initialCaseFields?.notes ?? ''}
              currentNotes={formFields.notes}
              onNotesChange={value => onFormFieldChange('notes', value)}
              onDiscard={onDiscardNotes}
            />
          </Box>
        </Box>

        {/* ── GREEN SECTION: Client / Employee Details ── */}
        <Box
          sx={{
            p: 2,
            bgcolor: '#e8f5e9',
            borderRadius: 1,
            border: '1px solid #a5d6a7',
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            color="success.dark"
            sx={{
              mb: 1.5,
              display: 'block',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {t('components.contactCenter.clientEmployeeDetails')}
          </Typography>
          <Box sx={TWO_COL_GRID}>
            <Box
              sx={
                isReportedByEmployeeSelected
                  ? DISABLED_FIELD_WRAPPER_SX
                  : undefined
              }
            >
              <Typography
                variant="body2"
                component="label"
                fontWeight={600}
                sx={{ mb: 0.5 }}
              >
                {t('components.contactCenter.reportedByName')}:
              </Typography>
              <Box
                component="input"
                type="text"
                value={formFields.reportedByName}
                onChange={e =>
                  onFormFieldChange('reportedByName', e.target.value)
                }
                disabled={isReportedByEmployeeSelected}
                sx={{
                  ...INPUT_STYLE,
                  ...(isReportedByEmployeeSelected && {
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed',
                  }),
                }}
              />
            </Box>
            <EditableCaseField
              label={t('components.contactCenter.reportedByEmployee')}
              value={String(formFields.reportedByEmployeeId ?? '')}
              onChange={() => {}}
              hasChanged={hasFieldChanged('reportedByEmployeeId')}
            >
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <CaseDropdown
                  value={formFields.reportedByEmployeeId ?? ''}
                  onChange={id => handleReportedBySelect(id as string)}
                  options={employeeOptions}
                  sx={{ ...getSelectFieldSx('reportedByEmployeeId'), flex: 1 }}
                />
                {formFields.reportedByEmployeeId != null && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() =>
                      onFormFieldChange('reportedByEmployeeId', null)
                    }
                    sx={{ minWidth: 0, px: 1, fontSize: '0.75rem' }}
                  >
                    ✕
                  </Button>
                )}
              </Box>
            </EditableCaseField>
            <EditableCaseField
              label={t('components.contactCenter.phoneNumber')}
              value={formFields.phoneNumber}
              onChange={v => onFormFieldChange('phoneNumber', v)}
              hasChanged={hasFieldChanged('phoneNumber')}
              type="tel"
              disabled={isReportedByEmployeeSelected}
            />
            <EditableCaseField
              label={t('components.contactCenter.email')}
              value={formFields.email}
              onChange={v => onFormFieldChange('email', v)}
              hasChanged={hasFieldChanged('email')}
              type="email"
              disabled={isReportedByEmployeeSelected}
            />
            <EditableCaseField
              label={t('components.contactCenter.employee')}
              value={String(formFields.employeeId ?? '')}
              onChange={() => {}}
              hasChanged={hasFieldChanged('employeeId')}
            >
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <CaseDropdown
                  value={formFields.employeeId ?? ''}
                  onChange={id =>
                    onFormFieldChange(
                      'employeeId',
                      typeof id === 'string' && id === '' ? null : Number(id)
                    )
                  }
                  options={employeeOptions}
                  sx={{ ...getSelectFieldSx('employeeId'), flex: 1 }}
                />
                {formFields.employeeId != null && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => onFormFieldChange('employeeId', null)}
                    sx={{ minWidth: 0, px: 1, fontSize: '0.75rem' }}
                  >
                    ✕
                  </Button>
                )}
              </Box>
            </EditableCaseField>
            <EditableCaseField
              label={t('components.contactCenter.clientContact')}
              value={formFields.clientContactId}
              onChange={() => {}}
              hasChanged={hasFieldChanged('clientContactId')}
            >
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <CaseDropdown
                  value={formFields.clientContactId || ''}
                  onChange={id =>
                    onFormFieldChange(
                      'clientContactId',
                      id == null || id === '' ? '' : String(id)
                    )
                  }
                  options={clientContactOptions}
                  sx={{ ...getSelectFieldSx('clientContactId'), flex: 1 }}
                />
                {formFields.clientContactId && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => onFormFieldChange('clientContactId', '')}
                    sx={{ minWidth: 0, px: 1, fontSize: '0.75rem' }}
                  >
                    ✕
                  </Button>
                )}
              </Box>
            </EditableCaseField>
          </Box>
        </Box>

        {/* ── PINK SECTION: Resolution ── */}
        <Box
          sx={{
            p: 2,
            bgcolor: '#fce4ec',
            borderRadius: 1,
            border: '1px solid #f48fb1',
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            color="error.dark"
            sx={{
              mb: 1.5,
              display: 'block',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {t('components.contactCenter.resolution')}
          </Typography>
          <Box sx={{ ...TWO_COL_GRID, gap: 1.5 }}>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <EditableCaseField
                label={t('components.contactCenter.resolutionDescription')}
                value={formFields.resolution}
                onChange={v => onFormFieldChange('resolution', v)}
                hasChanged={hasFieldChanged('resolution')}
                multiline
                headerAction={fillCaseResolutionButton}
              />
            </Box>
            <Box>
              <EditableCaseField
                label={t('components.contactCenter.closeCaseLoop')}
                value={formFields.closeCaseLoop}
                onChange={() => {}}
                hasChanged={hasFieldChanged('closeCaseLoop')}
              >
                <CaseDropdown
                  value={formFields.closeCaseLoop}
                  onChange={id =>
                    onFormFieldChange('closeCaseLoop', id as string)
                  }
                  options={[
                    { value: 'Phone', label: 'Phone' },
                    { value: 'Chat', label: 'Chat' },
                  ]}
                  sx={getSelectFieldSx('closeCaseLoop')}
                />
              </EditableCaseField>
            </Box>
            <Box>
              <EditableCaseField
                label={t('components.contactCenter.resolutionType')}
                value={formFields.resolutionType}
                onChange={() => {}}
                hasChanged={hasFieldChanged('resolutionType')}
              >
                <CaseDropdown
                  value={formFields.resolutionType}
                  onChange={id =>
                    onFormFieldChange('resolutionType', id as string)
                  }
                  options={resolutionTypeOptions}
                  sx={getSelectFieldSx('resolutionType')}
                />
              </EditableCaseField>
            </Box>
          </Box>
        </Box>

        {/* ── BLUE SECTION: Time Tracking ── */}
        {/* The drawer stays mounted when closed, so mount this per opening and per case:
            records logged at hang-up show up, and an edit left open does not carry over. */}
        {open && currentCase?.id != null && (
          <CaseTimeTrackingSection
            key={currentCase.id}
            caseId={currentCase.id}
          />
        )}
      </Box>
    );
  };

  return (
    <>
      <Snackbar
        open={!!toast}
        onClose={() => setToast(null)}
        message={toast?.message}
        variant="toast"
        severity={toast?.type ?? 'info'}
        autoHideDuration={3000}
      />
      {canLinkRelatedCase && (
        <LinkCaseDialog
          workspaceId={relatedCaseWorkspaceId}
          open={isRelatedCasePickerOpen}
          onClose={() => setIsRelatedCasePickerOpen(false)}
          onSelect={caseItem => void handleRelatedCaseSelected(caseItem)}
          excludeCaseId={mode === 'edit' ? currentCase?.id : null}
          isSubmitting={isLinkingRelatedCase}
        />
      )}
      <ConfirmDiscardChangesDialog
        open={isDiscardConfirmOpen}
        onDiscard={handleDiscardAndClose}
        onKeepEditing={() => setIsDiscardConfirmOpen(false)}
      />
      <Drawer
        anchor="right"
        variant="persistent"
        open={open}
        onClose={handleRequestClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            maxWidth: '100%',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Box
            data-testid="drawer-resize-handle"
            onPointerDown={handleResizeStart}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: 6,
              height: '100%',
              cursor: 'col-resize',
              zIndex: 2,
              '&:hover': { backgroundColor: 'primary.main', opacity: 0.35 },
            }}
          />
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography variant="h6" component="div" sx={{ fontSize: '1rem' }}>
              {title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {canLinkRelatedCase && (
                <Button
                  onClick={() => setIsRelatedCasePickerOpen(true)}
                  variant="contained"
                  size="small"
                  disabled={isLinkingRelatedCase}
                >
                  {t('components.contactCenter.linkToRelatedCase')}
                </Button>
              )}
              <Button
                onClick={handleRequestClose}
                variant="text"
                size="small"
                aria-label={t('common.close')}
              >
                ✕
              </Button>
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
            {mode === 'create' ? (
              renderCreateFields()
            ) : isCaseLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : caseLoadError ? (
              <Typography variant="body2" color="error.main">
                {t('components.contactCenter.errorLoadingCaseDetails')}
              </Typography>
            ) : (
              renderEditFields()
            )}
          </Box>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button
              onClick={handleRequestClose}
              variant={mode === 'create' ? 'outlined' : 'text'}
            >
              {mode === 'create' ? t('common.cancel') : t('common.close')}
            </Button>
            {mode === 'create' ? (
              <Button
                onClick={() => onSubmit()}
                variant="contained"
                disabled={isLoading}
              >
                {submitButtonLabel}
              </Button>
            ) : (
              <Button
                onClick={() => onSubmit()}
                variant="contained"
                color={isClosing ? 'error' : 'primary'}
                disabled={isLoading || !hasUnsavedChanges}
              >
                {submitButtonLabel}
              </Button>
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
